import { useCallback, useEffect, useState } from "react";

/**
 * Read + write the "hub.notif.sound" localStorage flag AND keep every
 * subscriber in sync in real time via a custom window event
 * (`hub.notif.sound.changed`).
 *
 * This lets the TopBar toggle switch flip the flag and every mounted
 * useMessageNotifications hook pick up the change on the very next incoming
 * message, without a page reload.
 *
 * Default: OFF (respect "do not disturb" as the user's implicit baseline).
 */
const KEY = "hub.notif.sound";
const EVT = "hub.notif.sound.changed";

const readInitial = () => {
    try { return localStorage.getItem(KEY) === "1"; }
    catch { return false; }
};

export function useNotificationSoundPref() {
    const [enabled, setEnabled] = useState(readInitial);

    // Listen for changes from other subscribers in the SAME tab (CustomEvent),
    // and also from other tabs (native `storage` event).
    useEffect(() => {
        const onCustom = (e) => setEnabled(!!e.detail);
        const onStorage = (e) => {
            if (e.key === KEY) setEnabled(e.newValue === "1");
        };
        window.addEventListener(EVT, onCustom);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(EVT, onCustom);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const setSoundEnabled = useCallback((next) => {
        try {
            if (next) localStorage.setItem(KEY, "1");
            else localStorage.removeItem(KEY);
        } catch { /* ignore quota / SSR */ }
        setEnabled(!!next);
        window.dispatchEvent(new CustomEvent(EVT, { detail: !!next }));
    }, []);

    return [enabled, setSoundEnabled];
}

/**
 * Play a short A5 (880 Hz) sine chime — same waveform used inside
 * useMessageNotifications when a new inbound message arrives. Exposed here
 * so the TopBar toggle can preview it on OFF → ON.
 */
export function previewNotifBeep() {
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = 880;
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.26);
    } catch { /* ignore */ }
}
